import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  Grid,
  GridItem,
  HStack,
  Flex,
  useBreakpointValue,
  FormErrorMessage,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Spinner,
  Text,
  ModalCloseButton,
  ModalFooter,
  Image,
  useToken,
  Checkbox,
} from "@chakra-ui/react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import DataTable from "./AppDataTable";
import { tableCustomStyles } from "../themes/TableStyles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileLines,
  faSyringe,
  faTable,
  faTrash,
  faCalculator,
  faGears,
  faCapsules,
  faMoneyBillWave,
  faDollarSign,
  faPrint,
  faVial,
} from "@fortawesome/free-solid-svg-icons";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import CustomDatePicker, { convertToThDate } from "./CustomDatePicker";
import * as IconButton from "../themes/IconButtonSet";
import Dropzone from "react-dropzone";
import { StandardPageTitle, StandardHeader } from "../themes/StandardHeader";
import { isValid } from "date-fns";

// Custom styles for DataTable
const customStyles = {
  header: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "18px",
      fontWeight: "bold",
    },
  },
  headCells: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "16px",
      fontWeight: "semibold",
      padding: "10px",
    },
  },
  cells: {
    style: {
      padding: "10px",
      fontSize: "14px",
    },
  },
  rows: {
    style: {
      "&:hover": {
        backgroundColor: "#f5f5f5",
      },
    },
  },
};

// แปลง base64 (data URL) เป็น Blob เพื่อใช้แสดงตัวอย่างและดาวน์โหลดไฟล์
const dataUrlToBlob = (dataUrl, fallbackType = "application/octet-stream") => {
  const [header, encoded] = String(dataUrl).split(",");
  const base64 = encoded ?? header;
  const typeFromHeader = encoded ? header.match(/data:([^;]+)/) : null;
  const byteCharacters = atob(base64);
  const bytes = new Uint8Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    bytes[i] = byteCharacters.charCodeAt(i);
  }
  return new Blob([bytes], {
    type: typeFromHeader ? typeFromHeader[1] : fallbackType,
  });
};

const downloadEvidenceFile = (file) => {
  if (!file?.data) return;
  const url = URL.createObjectURL(dataUrlToBlob(file.data, file.type));
  const link = document.createElement("a");
  link.href = url;
  link.download = file.name || "payment-evidence";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
};

// ต่ำกว่า lg ตารางรายการวัคซีนจะกว้างเกินจอ จึงสลับไปใช้การ์ดแทน
const VaccineDetailCards = ({
  details,
  isExisting,
  onQuantityChange,
  onRemove,
}) => {
  if (!details || details.length === 0) {
    return (
      <Text color="gray.500" textAlign="center" py={6}>
        ไม่มีรายการวัคซีน
      </Text>
    );
  }

  return (
    <VStack spacing={3} align="stretch">
      {details.map((row) => (
        <Box
          key={`${row.VaccineID}-${row.LotID}`}
          borderWidth={1}
          borderRadius="lg"
          boxShadow="sm"
          p={3}
        >
          <Flex justify="space-between" align="flex-start" gap={2} minW={0}>
            <Box minW={0} flex="1">
              <Text fontWeight="bold" noOfLines={2}>
                {row.name}
              </Text>
              <Text fontSize="xs" color="gray.500">
                {row.VaccineID} | Lot {row.LotID}
              </Text>
            </Box>
            {!isExisting && (
              <IconButton.DeleteIcon
                size="sm"
                aria-label="ลบ"
                title="ลบ"
                flexShrink={0}
                onClick={() => onRemove(row)}
              />
            )}
          </Flex>

          <Grid
            templateColumns="auto 1fr"
            gapX={2}
            gapY={2}
            mt={3}
            pt={3}
            borderTopWidth="1px"
            borderColor="gray.200"
            fontSize="sm"
            color="gray.600"
            alignItems="center"
          >
            <GridItem fontWeight="medium">จำนวน (ขวด):</GridItem>
            <GridItem>
              {isExisting ? (
                (row.RequestQuantity || 0).toLocaleString()
              ) : (
                <Input
                  type="number"
                  value={row.RequestQuantity}
                  onChange={(e) =>
                    onQuantityChange(row.VaccineID, row.LotID, e.target.value)
                  }
                  min={0}
                  size="sm"
                  maxW="120px"
                />
              )}
            </GridItem>

            <GridItem fontWeight="medium">จำนวน (โดส):</GridItem>
            <GridItem>{(row.DoseCount || 0).toLocaleString()}</GridItem>

            <GridItem fontWeight="medium">ราคา/ขวด:</GridItem>
            <GridItem>{(row.UnitPrice || 0).toLocaleString()}</GridItem>

            <GridItem fontWeight="medium">ราคารวม (บาท):</GridItem>
            <GridItem>{(row.TotalPrice || 0).toLocaleString()}</GridItem>
          </Grid>
        </Box>
      ))}
    </VStack>
  );
};

// Utility function to convert number to Thai text
const numberToThaiText = (number) => {
  const thaiDigits = [
    "ศูนย์",
    "หนึ่ง",
    "สอง",
    "สาม",
    "สี่",
    "ห้า",
    "หก",
    "เจ็ด",
    "แปด",
    "เก้า",
  ];
  const thaiUnits = ["", "สิบ", "ร้อย", "พัน", "หมื่น", "แสน", "ล้าน"];
  const thaiBahtUnits = ["บาท", "สตางค์"];

  if (number === 0) return "ศูนย์บาทถ้วน";

  const formatNumber = (num, unitIndex) => {
    let result = "";
    const digits = num.toString().split("").reverse();
    for (let i = 0; i < digits.length; i++) {
      const digit = parseInt(digits[i]);
      if (digit !== 0) {
        if (i === 1 && digit === 1) {
          result = "สิบ" + result;
        } else if (i === 1 && digit === 2) {
          result = "ยี่สิบ" + result;
        } else {
          result = thaiDigits[digit] + thaiUnits[i] + result;
        }
      }
    }
    return result;
  };

  const [baht, satang] = number.toFixed(2).split(".");
  let bahtText = "";
  let satangText = "";

  if (parseInt(baht) > 0) {
    bahtText = formatNumber(parseInt(baht), 0) + thaiBahtUnits[0];
  }

  if (parseInt(satang) > 0) {
    satangText = formatNumber(parseInt(satang), 0) + thaiBahtUnits[1];
  }

  let result = bahtText;
  if (satangText) {
    result += satangText;
  } else {
    result += "ถ้วน";
  }

  return result || "ศูนย์บาทถ้วน";
};

// จำนวนวันก่อนหมดอายุที่ยังขายได้แต่ควรเตือนเจ้าหน้าที่
const NEAR_EXPIRY_DAYS = 30;

// เพดานขนาดหลักฐานการชำระเงินต้องตรงกับฝั่ง API ไม่งั้นจะบันทึกไม่ผ่านโดยไม่รู้สาเหตุ
const PAYMENT_EVIDENCE_MAX_LENGTH = 1500000;

// Lot ที่ไม่มีวันหมดอายุถือว่าขายได้ ให้ตรงกับเงื่อนไขฝั่ง API
const isLotSellable = (item) =>
  item.ExpiryDays === null ||
  item.ExpiryDays === undefined ||
  item.ExpiryDays >= 0;

// CustomDatePicker คืนค่าเป็นสตริง yyyy-MM-dd ส่วนการโหลดเอกสารเดิมคืนเป็น Date
// ถ้าไม่แปลงให้เป็นชนิดเดียวกัน ใบเสร็จจะพิมพ์วันที่เป็นขีดกลางเมื่อผู้ใช้เปลี่ยนวัน
const toValidDate = (value) => {
  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }
  if (typeof value === "string" && value) {
    const parsed = new Date(value);
    return isValid(parsed) ? parsed : null;
  }
  return null;
};

const VaccineSaleForm = () => {
  const navigate = useNavigate();
  const { transactionId } = useParams();
  const location = useLocation();
  const { sessionId, profile } = useAuth();
  const isMobile = useBreakpointValue({ base: true, lg: false }, { ssr: false });
  // native <option> รับได้เฉพาะค่าสี CSS จริง จึงต้องดึงค่าจาก theme token ออกมาก่อน
  const [inStockColor, outOfStockColor, nearExpiryColor] = useToken("colors", [
    "green.600",
    "red.500",
    "orange.500",
  ]);
  const [formData, setFormData] = useState({
    TransactionID: transactionId || "SALE-XXXX-XX-XX",
    FarmerID: "",
    FarmerName: "",
    FarmerTel: "",
    FarmerEmail: "",
    UsageObjective: "",
    PickupPoint: "",
    ReservedBy: profile?.Email || "",
    RequestDate: new Date(),
    VaccineDetails: [],
    paymentEvidence: [], // Array to store uploaded files
  });

  const [pickupPoints, setPickupPoints] = useState([]);
  const [newVaccine, setNewVaccine] = useState({
    id: "",
    lotId: "",
    unitreq: "",
  });
  const [vaccineOptions, setVaccineOptions] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInventoryLoading, setIsInventoryLoading] = useState(false); // New loading state for Lot inventory
  const [isExisting, setIsExisting] = useState(!!transactionId);
  const [errors, setErrors] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false); // State ใหม่สำหรับ Modal ยืนยัน
  // ใช้ ref แทน state เพราะต้องกันการกดซ้ำได้ทันทีโดยไม่รอ re-render
  const isSavingRef = useRef(false);
  // ใบจำหน่ายเก็บชื่อ เบอร์โทร และเลขประจำตัวประชาชน จึงต้องบันทึกความยินยอมไว้ด้วย
  const [isConsentGiven, setIsConsentGiven] = useState(false);

  // เบราว์เซอร์ส่วนใหญ่ไม่ยอมแสดง PDF จาก data URL ใน iframe จึงต้องแปลงเป็น blob URL ก่อน
  useEffect(() => {
    if (!isModalOpen || !selectedFile?.data) {
      setPreviewUrl("");
      return undefined;
    }

    let url = "";
    try {
      url = URL.createObjectURL(
        dataUrlToBlob(selectedFile.data, selectedFile.type)
      );
      setPreviewUrl(url);
    } catch (error) {
      console.error("ไม่สามารถสร้างตัวอย่างไฟล์ได้:", error);
      setPreviewUrl("");
    }

    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [isModalOpen, selectedFile]);

  // Fetch every Lot in the pickup warehouse in one request.
  // โหลดทั้งคลังครั้งเดียวทำให้ dropdown วัคซีนบอกได้ว่าตัวไหนมีของ
  // โดยไม่ต้องยิง API ใหม่ทุกครั้งที่ผู้ใช้เปลี่ยนวัคซีน
  const fetchWarehouseInventory = async (warehouseId) => {
    console.log("Fetching warehouse inventory for:", {
      warehouseId,
      sessionId,
    });
    if (!warehouseId || !sessionId) {
      console.warn("Missing required parameters for fetchWarehouseInventory:", {
        warehouseId,
        sessionId,
      });
      return [];
    }

    setIsInventoryLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineInventoryByLot",
        data: { WarehouseID: warehouseId },
        sessionId: sessionId || "",
      });
      console.log("getVaccineInventoryByLot response:", response);
      if (response.result === "success" && response.InventoryReport) {
        setInventory(response.InventoryReport);
        return response.InventoryReport;
      }
      console.warn("API returned non-success result:", response);
      return [];
    } catch (error) {
      console.error("Error fetching warehouse inventory:", error.message);
      return [];
    } finally {
      setIsInventoryLoading(false);
    }
  };

  // Update useEffect to fetch initial data
  useEffect(() => {
    const loadData = async () => {
      if (sessionId) {
        console.log("Loading initial data with sessionId:", sessionId);
        await fetchPickupPoints();
        const vaccines = await fetchVaccineOptions();
        if (transactionId) {
          await fetchSale(transactionId, vaccines);
        }
      } else {
        console.warn("No sessionId, redirecting to login");
      }
    };
    loadData();
  }, [sessionId, transactionId]);

  // useEffect for fetching Lot inventory
  useEffect(() => {
    console.log("useEffect triggered for inventory fetch:", {
      pickupPoint: formData.PickupPoint,
    });
    if (formData.PickupPoint && sessionId) {
      fetchWarehouseInventory(formData.PickupPoint);
    }
  }, [formData.PickupPoint, sessionId]);

  // Handle file drop for payment evidence
  const handleFileDrop = (acceptedFiles) => {
    const maxFileSize = 2 * 1024 * 1024; // 2MB in bytes (เพิ่มขนาดขึ้น)
    
    acceptedFiles.forEach((file) => {
      // ตรวจสอบขนาดไฟล์
      if (file.size > maxFileSize) {
        alert(`ไฟล์ "${file.name}" มีขนาดใหญ่เกินไป (${(file.size / 1024 / 1024).toFixed(2)}MB) กรุณาเลือกไฟล์ที่มีขนาดไม่เกิน 2MB`);
        return;
      }
      
      const reader = new FileReader();
      reader.onload = () => {
        const base64Data = reader.result;
        
        // ตรวจสอบขนาดของ base64 data
        const base64Size = base64Data.length;
        console.log("Base64 data size:", base64Size, "characters");
        
        if (base64Size > 1000000) { // 1MB limit for base64 (เพิ่มขนาดขึ้น)
          alert(`ไฟล์ "${file.name}" หลังจากแปลงเป็น base64 มีขนาดใหญ่เกินไป กรุณาเลือกไฟล์ที่มีขนาดไม่เกิน 2MB`);
          return;
        }
        
        const fileData = {
          name: file.name,
          type: file.type,
          data: base64Data,
          size: file.size, // เก็บขนาดไฟล์ไว้ด้วย
        };
        setFormData((prev) => ({
          ...prev,
          paymentEvidence: [...prev.paymentEvidence, fileData],
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  // Handle file view
  const handleViewFile = (file) => {
    setSelectedFile(file);
    setIsModalOpen(true);
  };

  // Print receipt function
  const printReceipt = () => {
    const getPickupPointName = (pickupPointId) => {
      const point = pickupPoints.find((p) => p.WarehouseID === pickupPointId);
      return point ? point.WarehouseName : "-";
    };

    const totalAmount = formData.VaccineDetails.reduce(
      (sum, detail) => sum + detail.TotalPrice,
      0
    );
    const thaiAmountText = numberToThaiText(totalAmount);

    const requestDate = toValidDate(formData.RequestDate);

    const printContent = `
      <html>
        <head>
          <title>Receipt - ${formData.TransactionID}</title>
          <style>
            body { font-family: Sarabun, Arial, sans-serif; margin: 40px; }
            h1, h2 { text-align: center; }
            .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; margin-bottom: 32px; }
            .grid-item { margin-bottom: 8px; }
            .grid-item span { font-weight: bold; }
            table { border-collapse: collapse; width: 100%; margin-bottom: 32px; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .total { text-align: right; font-weight: bold; margin-bottom: 16px; }
            .thai-text { text-align: right; margin-bottom: 32px; }
            .signature { text-align: right; margin-top: 32px; }
          </style>
        </head>
        <body>
          <h1>ใบเสร็จรับเงิน</h1>
          <div class="grid">
            <div class="grid-item">
              <span>เลขที่เอกสาร:</span> ${formData.TransactionID || "-"}
            </div>
            <div class="grid-item">
              <span>วันที่จำหน่าย:</span> ${requestDate ? convertToThDate(requestDate) : "-"}
            </div>
            <div class="grid-item">
              <span>จุดจำหน่าย:</span> ${getPickupPointName(formData.PickupPoint) || "-"}
            </div>
            <div class="grid-item">
              <span>เลขประจำตัวประชาชน:</span> ${formData.FarmerID || "-"}
            </div>
            <div class="grid-item">
              <span>ชื่อเกษตรกร:</span> ${formData.FarmerName || "-"}
            </div>
            <div class="grid-item">
              <span>เบอร์โทรติดต่อ:</span> ${formData.FarmerTel || "-"}
            </div>
            <div class="grid-item">
              <span>E-mail Address:</span> ${formData.FarmerEmail || "-"}
            </div>
            <div class="grid-item" style="grid-column: 1 / -1;">
              <span>วัตถุประสงค์การใช้งาน:</span> ${formData.UsageObjective || "-"}
            </div>
          </div>
          <h2>รายการวัคซีนที่จำหน่าย</h2>
          <table>
            <thead>
              <tr>
                <th>รหัสวัคซีน</th>
                <th>ชื่อวัคซีน</th>
                <th>Lot No.</th>
                <th>จำนวน (ขวด)</th>
                <th>จำนวนโดส</th>
                <th>ราคาต่อหน่วย</th>
                <th>มูลค่ารวม</th>
              </tr>
            </thead>
            <tbody>
              ${
                formData.VaccineDetails.length > 0
                  ? formData.VaccineDetails.map(
                      (detail) => `
                        <tr>
                          <td>${detail.VaccineID || "-"}</td>
                          <td>${detail.name || "-"}</td>
                          <td>${detail.LotID || "-"}</td>
                          <td>${detail.RequestQuantity.toLocaleString()}</td>
                          <td>${detail.DoseCount.toLocaleString()}</td>
                          <td>${detail.UnitPrice.toLocaleString()}</td>
                          <td>${detail.TotalPrice.toLocaleString()}</td>
                        </tr>
                      `
                    ).join("")
                  : `
                        <tr>
                          <td colspan="7" style="text-align: center;">ไม่มีรายการวัคซีน</td>
                        </tr>
                    `
              }
            </tbody>
          </table>
          <div class="total">
            รวมทั้งสิ้น: ${totalAmount.toLocaleString()} บาท
          </div>
          <div class="thai-text">
            (${thaiAmountText})
          </div>
          <div class="signature">
            ___________________________<br>
            ผู้รับเงิน
          </div>
        </body>
      </html>
    `;
    const newWindow = window.open("", "", "width=800,height=600");
    if (!newWindow) {
      alert("ไม่สามารถเปิดหน้าต่างพิมพ์ได้ กรุณาปิดการบล็อกป๊อปอัป");
      return;
    }
    newWindow.document.write(printContent);
    newWindow.document.close();
    newWindow.print();
  };

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const fetchPickupPoints = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinePickupPointList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setPickupPoints(response.VaccineWarehousesList || []);
      }
    } catch (error) {
      console.error("Error fetching pickup points:", error);
    }
  };

  const fetchVaccineOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setVaccineOptions(response.VaccinesList || []);
        return response.VaccinesList || [];
      }
      return [];
    } catch (error) {
      console.error("Error fetching vaccines:", error);
      return [];
    }
  };

  const fetchSale = async (id, vaccines) => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineSaleOrdersByID",
        data: { TransactionID: id },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const sale = response.VaccineSaleOrder;
        const details = response.VaccineDetails || [];
        
        // Debug: Log the entire response
        console.log("API Response:", response);
        console.log("Sale object:", sale);
        console.log("PaymentEvidence from API:", sale.paymentEvidence);
        // Debug: Log payment evidence data
        console.log("Loaded payment evidence:", sale.paymentEvidence);
        console.log("Payment evidence length:", sale.paymentEvidence?.length || 0);
        console.log("Payment evidence type:", typeof sale.paymentEvidence);
        console.log("Payment evidence structure:", JSON.stringify(sale.paymentEvidence, null, 2));
        
        // Validate and clean payment evidence data
        let validatedPaymentEvidence = [];
        if (sale.paymentEvidence && Array.isArray(sale.paymentEvidence)) {
          // Handle nested JSON strings and filter out any invalid entries
          const processedItems = [];
          
          sale.paymentEvidence.forEach(item => {
            if (typeof item === 'string') {
              // Try to parse JSON string
              try {
                const parsed = JSON.parse(item);
                if (Array.isArray(parsed)) {
                  // If parsed item is an array, add all items
                  processedItems.push(...parsed);
                } else if (parsed && typeof parsed === 'object') {
                  // If parsed item is an object, add it
                  processedItems.push(parsed);
                }
              } catch (parseError) {
                console.warn("Failed to parse PaymentEvidence item in frontend:", item, parseError);
              }
            } else if (item && typeof item === 'object') {
              // Direct object
              processedItems.push(item);
            }
          });
          
          // Filter out any invalid entries
          const validItems = processedItems.filter(item => 
            item && 
            typeof item === 'object' && 
            item.name && 
            item.type && 
            item.data &&
            typeof item.name === 'string' &&
            typeof item.type === 'string' &&
            typeof item.data === 'string'
          );
          
          // Remove duplicates based on file name and data
          const uniqueItems = [];
          const seenItems = new Set();
          
          validItems.forEach(item => {
            const key = `${item.name}_${item.data.substring(0, 100)}`; // Use first 100 chars of data as key
            if (!seenItems.has(key)) {
              seenItems.add(key);
              uniqueItems.push(item);
            } else {
              console.log("Removing duplicate item in frontend:", item.name);
            }
          });
          
          validatedPaymentEvidence = uniqueItems;
        }
        console.log("Validated payment evidence:", validatedPaymentEvidence);
        console.log("Validated payment evidence length:", validatedPaymentEvidence.length);
        console.log("Validated payment evidence structure:", JSON.stringify(validatedPaymentEvidence, null, 2));
        console.log("Validated payment evidence is array:", Array.isArray(validatedPaymentEvidence));
        console.log("Details array:", details);
        console.log("Details length:", details.length);

        const updatedDetails = details.map((detail) => {
          const vaccine = vaccines.find(
            (v) => v.VaccineID === detail.VaccineID
          );
          const packageSize = vaccine?.PackageSize || 1;
          const doseCount = Math.ceil(detail.RequestQuantity * packageSize);
          return {
            VaccineID: detail.VaccineID,
            name: vaccine ? vaccine.VaccineNameTH : "ไม่พบชื่อวัคซีน",
            LotID: detail.LotID,
            RequestQuantity: detail.RequestQuantity,
            UnitPrice: detail.UnitPrice,
            TotalPrice: detail.TotalPrice,
            DoseCount: doseCount,
          };
        });
        
        const newFormData = {
          TransactionID: sale.TransactionID,
          FarmerID: sale.FarmerID || "",
          FarmerName: sale.FarmerName || "",
          FarmerTel: sale.FarmerTel || "",
          FarmerEmail: sale.FarmerEmail || "",
          UsageObjective: sale.UsageObjective || "",
          PickupPoint: sale.PickupPoint || "",
          ReservedBy: sale.ReservedBy || "",
          RequestDate: sale.RequestDate ? new Date(sale.RequestDate) : new Date(),
          VaccineDetails: updatedDetails,
          paymentEvidence: validatedPaymentEvidence, // Load existing files (validated)
        };
        
        console.log("Setting formData:", newFormData);
        console.log("FormData VaccineDetails length:", newFormData.VaccineDetails.length);
        console.log("FormData paymentEvidence length:", newFormData.paymentEvidence.length);
        console.log("FormData paymentEvidence is array:", Array.isArray(newFormData.paymentEvidence));
        
        setFormData(newFormData);
        
        // Debug: Check if formData was set correctly
        setTimeout(() => {
          console.log("FormData after setting:", formData);
        }, 100);
      }
    } catch (error) {
      console.error("Error fetching sale:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log("Form input change:", { name, value });
    console.log("Current formData:", formData);
    if (name === "PickupPoint" && formData.VaccineDetails.length > 0) {
      alert("ไม่สามารถแก้ไขจุดจำหน่ายได้ เนื่องจากมีรายการวัคซีนแล้ว");
      return;
    }
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "PickupPoint") {
        // Reset vaccine selection when PickupPoint changes
        setNewVaccine({ id: "", lotId: "", unitreq: "" });
        //setInventory([]);
      }
      return updated;
    });
    validateField(name, value);
  };

  const handleVaccineInputChange = (e) => {
    const { name, value } = e.target;
    console.log("Vaccine input change:", { name, value });
    if (name === "id" && !formData.PickupPoint) {
      alert("กรุณาเลือกจุดจำหน่ายก่อนเลือกวัคซีน");
      return;
    }
    setNewVaccine((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "id") {
        // Reset lotId and unitreq when vaccine changes
        return { ...updated, lotId: "", unitreq: "" };
      }
      return updated;
    });
  };

  const handleQuantityChange = async (vaccineId, lotId, value) => {
    const newQuantity = value === "" ? 0 : parseInt(value, 10);
    console.log("handleQuantityChange:", { vaccineId, lotId, newQuantity });
    console.log("Current inventory:", inventory);

    // Find the Lot in inventory
    let selectedLot = inventory.find(
      (item) => item.VaccineID === vaccineId && item.LotID === lotId
    );

    // If Lot not found, try fetching it
    if (!selectedLot && formData.PickupPoint && sessionId) {
      console.log(
        `Lot ${lotId} not found, fetching inventory for VaccineID ${vaccineId}`
      );
      const refreshedInventory = await fetchWarehouseInventory(
        formData.PickupPoint
      );
      selectedLot = refreshedInventory.find(
        (item) => item.VaccineID === vaccineId && item.LotID === lotId
      );
    }
    if (!selectedLot) {
      console.warn(
        `Lot ${lotId} not found for VaccineID ${vaccineId} in inventory`
      );
      alert(`ไม่พบ Lot ${lotId} สำหรับวัคซีน ${vaccineId} ในคลัง`);
      return;
    }

    // Validate quantity against TotalQuantity
    if (newQuantity > selectedLot.TotalQuantity) {
      alert(
        `จำนวนที่ขอจำหน่าย (${newQuantity.toLocaleString()}) เกินจำนวนคงคลังของ Lot ${lotId} (${selectedLot.TotalQuantity.toLocaleString()})`
      );
      return;
    }

    // Update VaccineDetails if quantity is valid
    const updatedDetails = formData.VaccineDetails.map((detail) => {
      if (detail.VaccineID === vaccineId && detail.LotID === lotId) {
        const packageSize =
          vaccineOptions.find((v) => v.VaccineID === vaccineId)?.PackageSize ||
          1;
        const newTotalPrice = newQuantity * detail.UnitPrice;
        const newDoseCount = Math.ceil(newQuantity * packageSize);
        return {
          ...detail,
          RequestQuantity: newQuantity,
          TotalPrice: newTotalPrice,
          DoseCount: newDoseCount,
        };
      }
      return detail;
    });
    console.log("Updating VaccineDetails:", updatedDetails);
    setFormData({ ...formData, VaccineDetails: updatedDetails });
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "PickupPoint":
      case "FarmerName":
      case "FarmerTel":
        if (!value) {
          newErrors[name] = "กรุณากรอกข้อมูล";
        } else {
          delete newErrors[name];
        }
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  const isFormValid = () => {
    return (
      formData.PickupPoint &&
      formData.FarmerName &&
      formData.FarmerTel &&
      Object.keys(errors).length === 0 &&
      formData.VaccineDetails.length > 0 // เพิ่มเงื่อนไขว่าต้องมีรายการวัคซีนอย่างน้อยหนึ่งรายการ
    );
  };

  const handleAddVaccine = () => {
    console.log("Attempting to add vaccine:", newVaccine);
    if (!newVaccine.id || !newVaccine.lotId) {
      alert("กรุณาเลือกวัคซีนและ Lot ที่ต้องการจำหน่าย");
      return;
    }
    if (!newVaccine.unitreq || newVaccine.unitreq <= 0) {
      alert("กรุณาระบุจำนวนที่มากกว่า 0");
      return;
    }

    // หนึ่งใบจำหน่ายต้องมีวัคซีน+Lot ละหนึ่งบรรทัด ถ้าซ้ำได้จะตัดสต็อกเกินจนคลังติดลบ
    const isDuplicate = formData.VaccineDetails.some(
      (detail) =>
        detail.VaccineID === newVaccine.id && detail.LotID === newVaccine.lotId
    );
    if (isDuplicate) {
      alert(
        `มีรายการวัคซีนนี้ Lot ${newVaccine.lotId} อยู่ในใบจำหน่ายแล้ว กรุณาแก้ไขจำนวนที่รายการเดิมแทนการเพิ่มรายการซ้ำ`
      );
      return;
    }

    const selectedVaccine = vaccineOptions.find(
      (v) => v.VaccineID === newVaccine.id
    );
    const selectedLot = inventory.find(
      (item) =>
        item.VaccineID === newVaccine.id && item.LotID === newVaccine.lotId
    );
    if (!selectedVaccine || !selectedLot) {
      alert("วัคซีนหรือ Lot ที่เลือกไม่พบ");
      return;
    }

    const requestQuantity = parseInt(newVaccine.unitreq);
    if (requestQuantity > selectedLot.TotalQuantity) {
      alert(
        `จำนวนที่ขอจำหน่าย (${requestQuantity.toLocaleString()}) เกินจำนวนคงคลังของ Lot ${
          selectedLot.LotID
        } (${selectedLot.TotalQuantity.toLocaleString()})`
      );
      return;
    }

    const packageSize = selectedVaccine.PackageSize || 1;
    const doseCount = Math.ceil(requestQuantity * packageSize);
    const unitPrice = selectedVaccine.PriceDistribution || 350;
    const totalPrice = requestQuantity * unitPrice;

    const vaccineData = {
      VaccineID: newVaccine.id,
      name: selectedVaccine.VaccineNameTH,
      LotID: newVaccine.lotId,
      RequestQuantity: requestQuantity,
      UnitPrice: unitPrice,
      TotalPrice: totalPrice,
      DoseCount: doseCount,
    };

    console.log("Adding vaccine to VaccineDetails:", vaccineData);
    setFormData({
      ...formData,
      VaccineDetails: [...formData.VaccineDetails, vaccineData],
    });
    setNewVaccine({ id: "", lotId: "", unitreq: "" });
  };

  const handleRemoveVaccine = async (row) => {
    setFormData({
        ...formData,
        VaccineDetails: formData.VaccineDetails.filter(
          (v) => v.VaccineID !== row.VaccineID || v.LotID !== row.LotID
        ),
      });
      // Refresh inventory for the removed vaccine
      if (formData.PickupPoint && sessionId) {
        await fetchWarehouseInventory(formData.PickupPoint);
      }
  };

const handleSave = async () => {
  // ใบจำหน่ายแก้ไขไม่ได้หลังบันทึก การยิงซ้ำจึงกลายเป็นสองใบที่ตัดสต็อกซ้ำและลบไม่ได้
  if (isSavingRef.current) {
    return;
  }

  if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วนและเพิ่มรายการวัคซีนที่จำหน่ายอย่างน้อยหนึ่งรายการ");
    return;
  }

  isSavingRef.current = true;
  setIsLoading(true);
  try {
        // Submit form data with paymentEvidence
        const submitData = { ...formData, ConsentGiven: isConsentGiven };
        
        // Validate and clean payment evidence before submission
        let validatedPaymentEvidence = [];
        if (submitData.paymentEvidence && Array.isArray(submitData.paymentEvidence)) {
          // Handle nested JSON strings and filter out any invalid entries
          const processedItems = [];
          
          submitData.paymentEvidence.forEach(item => {
            if (typeof item === 'string') {
              // Try to parse JSON string
              try {
                const parsed = JSON.parse(item);
                if (Array.isArray(parsed)) {
                  // If parsed item is an array, add all items
                  processedItems.push(...parsed);
                } else if (parsed && typeof parsed === 'object') {
                  // If parsed item is an object, add it
                  processedItems.push(parsed);
                }
              } catch (parseError) {
                console.warn("Failed to parse PaymentEvidence item before submission:", item, parseError);
              }
            } else if (item && typeof item === 'object') {
              // Direct object
              processedItems.push(item);
            }
          });
          
          // Filter out any invalid entries
          const validItems = processedItems.filter(item => 
            item && 
            typeof item === 'object' && 
            item.name && 
            item.type && 
            item.data &&
            typeof item.name === 'string' &&
            typeof item.type === 'string' &&
            typeof item.data === 'string'
          );
          
          // Remove duplicates based on file name and data
          const uniqueItems = [];
          const seenItems = new Set();
          
          validItems.forEach(item => {
            const key = `${item.name}_${item.data.substring(0, 100)}`; // Use first 100 chars of data as key
            if (!seenItems.has(key)) {
              seenItems.add(key);
              uniqueItems.push(item);
            } else {
              console.log("Removing duplicate item before submission:", item.name);
            }
          });
          
          validatedPaymentEvidence = uniqueItems;
        }
        submitData.paymentEvidence = validatedPaymentEvidence;

        // API เก็บหลักฐานทั้งหมดไว้ในคอลัมน์เดียว ถ้าเกินเพดานจะบันทึกไม่ผ่าน
        // ต้องดักตรงนี้ก่อน ไม่งั้นผู้ใช้จะเสียเวลากรอกทั้งใบแล้วมาติดตอนท้าย
        const evidenceSize = JSON.stringify(validatedPaymentEvidence).length;
        if (evidenceSize > PAYMENT_EVIDENCE_MAX_LENGTH) {
          alert(
            `ไฟล์หลักฐานการชำระเงินมีขนาดรวม ${(evidenceSize / 1048576).toFixed(
              2
            )} MB เกินที่ระบบรองรับ ${(
              PAYMENT_EVIDENCE_MAX_LENGTH / 1048576
            ).toFixed(2)} MB กรุณาลบไฟล์บางส่วนหรือใช้ไฟล์ที่มีขนาดเล็กลง`
          );
          return;
        }

        console.log("Submitting paymentEvidence count:", validatedPaymentEvidence.length);
    const response = await apiService({
      method: "POST",
      url: "/createVaccineSaleOrders",
      data: submitData,
      sessionId: sessionId || "",
    });

    if (response.result !== "success") {
      alert(
        "ไม่สามารถบันทึกใบจำหน่ายได้: " +
          (response.error || "ไม่ทราบสาเหตุ กรุณาติดต่อผู้ดูแลระบบ")
      );
      return;
    }

    const transactionId = response.TransactionID;
    setFormData({ ...formData, TransactionID: transactionId });
    setIsExisting(true);
    setIsConfirmModalOpen(false);

    // ยืนยันจำนวนหลักฐานที่บันทึกจริง เพื่อให้จับได้ทันทีถ้าไฟล์ไม่ครบ
    const savedEvidenceCount =
      response.PaymentEvidenceCount ?? validatedPaymentEvidence.length;
    alert(
      `บันทึกใบจำหน่ายสำเร็จ! เลขที่เอกสาร: ${transactionId}\n` +
        `บันทึกหลักฐานการชำระเงิน ${savedEvidenceCount} จาก ${validatedPaymentEvidence.length} ไฟล์`
    );
  } catch (error) {
    console.error("API Error:", error.message);
    alert("เกิดข้อผิดพลาดในการบันทึก: " + error.message);
  } finally {
    isSavingRef.current = false;
    setIsLoading(false);
  }
};

  // ฟังก์ชันสำหรับเปิด Modal ยืนยัน
  const handleOpenConfirmModal = () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วนและเพิ่มรายการวัคซีนที่จำหน่ายอย่างน้อยหนึ่งรายการ");
      return;
    }
    setIsConsentGiven(false);
    setIsConfirmModalOpen(true);
  };

  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faSyringe} />
          <span>รหัส / ชื่อวัคซีน</span>
        </HStack>
      ),
      selector: (row) => row.VaccineID,
      sortable: true,
      cell: (row) => (
        <Box py={1}>
          <Text fontWeight="medium">{row.VaccineID}</Text>
          <Text fontSize="sm" color="gray.600">
            {row.name}
          </Text>
        </Box>
      ),
      minWidth: "240px",
      wrap: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faVial} />
          <span>Lot No.</span>
        </HStack>
      ),
      selector: (row) => row.LotID,
      sortable: true,
      minWidth: "140px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faCalculator} />
          <span>จำนวน (ขวด)</span>
        </HStack>
      ),
      selector: (row) => row.RequestQuantity,
      sortable: true,
      cell: (row) => (
        <Box>
          {isExisting ? (
            row.RequestQuantity.toLocaleString()
          ) : (
            <Input
              type="number"
              value={row.RequestQuantity}
              onChange={(e) =>
                handleQuantityChange(row.VaccineID, row.LotID, e.target.value)
              }
              min={0}
              size="sm"
              width="100px"
            />
          )}
        </Box>
      ),
      minWidth: "130px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faCapsules} />
          <span>จำนวน (โดส)</span>
        </HStack>
      ),
      selector: (row) => row.DoseCount,
      sortable: true,
      cell: (row) => <Box>{row.DoseCount.toLocaleString()}</Box>,
      minWidth: "120px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faDollarSign} />
          <span>ราคา/ขวด</span>
        </HStack>
      ),
      selector: (row) => row.UnitPrice,
      sortable: true,
      cell: (row) => <Box>{row.UnitPrice.toLocaleString()}</Box>,
      minWidth: "140px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faMoneyBillWave} />
          <span>ราคารวม (บาท)</span>
        </HStack>
      ),
      selector: (row) => row.TotalPrice,
      sortable: true,
      cell: (row) => <Box>{row.TotalPrice.toLocaleString()}</Box>,
      minWidth: "130px",
    },
    // เอกสารที่บันทึกแล้วแก้ไขไม่ได้ จึงไม่ต้องมีคอลัมน์การดำเนินการ
    ...(isExisting
      ? []
      : [
          {
            name: (
              <HStack>
                <FontAwesomeIcon icon={faGears} />
                <span>จัดการ</span>
              </HStack>
            ),
            cell: (row) => (
              <IconButton.Delete
                icon={faTrash}
                onClick={() => handleRemoveVaccine(row)}
                label="ลบ"
                size="sm"
              />
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            minWidth: "130px",
          },
        ]),
  ];

  const totalBottles = (formData.VaccineDetails || []).reduce(
    (sum, detail) => sum + (detail.RequestQuantity || 0),
    0
  );
  const totalValue = (formData.VaccineDetails || []).reduce(
    (sum, detail) => sum + (detail.TotalPrice || 0),
    0
  );

  // ข้อมูลที่ต้องเห็นครบก่อนกดยืนยัน เพราะเอกสารแก้ไขไม่ได้หลังบันทึก
  const confirmSummary = {
    pickupPointName:
      pickupPoints.find((point) => point.WarehouseID === formData.PickupPoint)
        ?.WarehouseName || formData.PickupPoint || "-",
    requestDateText: toValidDate(formData.RequestDate)
      ? convertToThDate(toValidDate(formData.RequestDate))
      : "-",
    evidenceCount: (formData.paymentEvidence || []).length,
    totalBottles,
    totalValue,
  };

  const isInventoryReady = Boolean(formData.PickupPoint) && !isInventoryLoading;

  // สรุปยอดที่ยังเพิ่มรายการได้ของวัคซีนแต่ละตัว เพื่อทำสีใน dropdown เลือกวัคซีน
  // นับเฉพาะ Lot ที่มีของและยังไม่ถูกเพิ่มลงในใบนี้ ให้ตรงกับเงื่อนไขของ availableLots
  const vaccineAvailability = useMemo(() => {
    const lotsInDocument = new Set(
      (formData.VaccineDetails || []).map(
        (detail) => `${detail.VaccineID}-${detail.LotID}`
      )
    );

    return inventory.reduce((summary, item) => {
      if (
        item.TotalQuantity > 0 &&
        isLotSellable(item) &&
        !lotsInDocument.has(`${item.VaccineID}-${item.LotID}`)
      ) {
        const current = summary.get(item.VaccineID) || { lots: 0, quantity: 0 };
        summary.set(item.VaccineID, {
          lots: current.lots + 1,
          quantity: current.quantity + item.TotalQuantity,
        });
      }
      return summary;
    }, new Map());
  }, [inventory, formData.VaccineDetails]);

  // Filter and sort Lots (FIFO by ProductionDate)
  // Lot ที่อยู่ในใบนี้แล้วจะไม่แสดงซ้ำ เพราะหนึ่งใบต้องมีวัคซีน+Lot ละหนึ่งบรรทัดเท่านั้น
  // Lot ที่หมดอายุแล้วไม่แสดง เพราะ API จะปฏิเสธและใบจำหน่ายแก้ไขทีหลังไม่ได้
  const availableLots = inventory
    .filter(
      (item) =>
        item.VaccineID === newVaccine.id &&
        item.TotalQuantity > 0 &&
        isLotSellable(item) &&
        !(formData.VaccineDetails || []).some(
          (detail) =>
            detail.VaccineID === item.VaccineID && detail.LotID === item.LotID
        )
    )
    .sort((a, b) => new Date(a.ProductionDate) - new Date(b.ProductionDate));

  const expiredLotCount = inventory.filter(
    (item) =>
      item.VaccineID === newVaccine.id &&
      item.TotalQuantity > 0 &&
      !isLotSellable(item)
  ).length;

  const selectedLotNearExpiry = availableLots.find(
    (lot) =>
      lot.LotID === newVaccine.lotId &&
      typeof lot.ExpiryDays === "number" &&
      lot.ExpiryDays <= NEAR_EXPIRY_DAYS
  );

  // Set default LotID when availableLots changes
  useEffect(() => {
    console.log("Available Lots updated:", availableLots);
    if (availableLots.length > 0 && !newVaccine.lotId && !isInventoryLoading) {
      console.log("Setting default LotID:", availableLots[0].LotID);
      setNewVaccine((prev) => ({ ...prev, lotId: availableLots[0].LotID }));
    }
  }, [availableLots, newVaccine.lotId, isInventoryLoading]);

  // Debug: Track formData changes
  useEffect(() => {
    console.log("FormData changed:", formData);
    console.log("FormData paymentEvidence:", formData.paymentEvidence);
    console.log("FormData paymentEvidence length:", formData.paymentEvidence?.length);
    console.log("FormData paymentEvidence is array:", Array.isArray(formData.paymentEvidence));
  }, [formData]);

  if (!sessionId) {
    return null;
  }

  if (isLoading && isExisting) {
    return (
      <Box
        p={{ base: 3, sm: 4, md: 6 }}
        m={{ base: 2, md: 4 }}
        borderWidth={1}
        borderRadius="lg"
        boxShadow="lg"
      >
        <StandardPageTitle title="รายการจำหน่ายวัคซีน" />
        <Box textAlign="center" py={20}>
          <Spinner size="xl" color="blue.500" />
          <Text mt={4} fontSize="lg">กำลังโหลดข้อมูล...</Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      p={{ base: 3, sm: 4, md: 6 }}
      m={{ base: 2, md: 4 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <StandardPageTitle title="รายการจำหน่ายวัคซีน" />
      <Box
        borderWidth={1}
        borderRadius="lg"
        boxShadow="lg"
        p={{ base: 3, md: 4 }}
        mb={{ base: 4, md: 6 }}
      >
        <StandardHeader
          title="ข้อมูลเอกสารการจำหน่ายวัคซีน"
          bgColor="iBlue.600"
          icon={faFileLines}
        />
        <Grid
          templateColumns={{
            base: "1fr",
            md: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          }}
          gap={{ base: 3, md: 4 }}
          pt={{ base: 3, md: 4 }}
        >
          <GridItem>
            <FormControl isInvalid={errors.TransactionID}>
              <FormLabel>เลขที่เอกสาร</FormLabel>
              <Input
                name="TransactionID"
                value={formData.TransactionID || ""}
                isReadOnly
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel>วันที่จำหน่าย</FormLabel>
              <CustomDatePicker
                  name="RequestDate"
                  value={formData.RequestDate}
                  onChange={handleInputChange}
                  minDate={
                    new Date(new Date().setDate(new Date().getDate() - 7))
                  } // 7 วันย้อนหลังจากวันนี้
                  maxDate={new Date()} // วันนี้
                  colorScheme="iBlue"
                  isReadOnly={isExisting}
                />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired isInvalid={errors.PickupPoint}>
              <FormLabel>จุดจำหน่าย</FormLabel>
              {isExisting ? (
                <Input
                  value={
                    pickupPoints.find(
                      (point) => point.WarehouseID === formData.PickupPoint
                    )?.WarehouseName ||
                    formData.PickupPoint ||
                    "-"
                  }
                  isReadOnly
                  colorScheme="iBlue"
                />
              ) : (
                <>
                <Select
                  name="PickupPoint"
                  value={formData.PickupPoint}
                  onChange={handleInputChange}
                  placeholder="เลือกจุดจำหน่าย"
                  isDisabled={formData.VaccineDetails.length > 0}
                >
                  {pickupPoints.map((point) => (
                    <option key={point.WarehouseID} value={point.WarehouseID}>
                      {point.WarehouseName}
                    </option>
                  ))}
                </Select>
                {formData.VaccineDetails.length > 0 && (
                  <Text color="red.500" fontSize="sm" mt={2}>
                    ไม่สามารถแก้ไขจุดจำหน่ายได้ เนื่องจากมีรายการวัคซีนแล้ว
                  </Text>
                  )}
                </>                
              )}
              <FormErrorMessage>{errors.PickupPoint}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel>เลขประจำตัวประชาชน</FormLabel>
              <Input
                name="FarmerID"
                value={formData.FarmerID}
                onChange={handleInputChange}
                colorScheme="iBlue"
                isReadOnly={isExisting}
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={{ base: 1, md: 2 }}>
            <FormControl isRequired isInvalid={!!errors.FarmerName}>
              <FormLabel>ชื่อเกษตรกร</FormLabel>
              <Input
                name="FarmerName"
                value={formData.FarmerName}
                onChange={handleInputChange}
                colorScheme="iBlue"
                isReadOnly={isExisting}
              />
              <FormErrorMessage>{errors.FarmerName}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl isRequired isInvalid={!!errors.FarmerTel}>
              <FormLabel>เบอร์โทรติดต่อ</FormLabel>
              <Input
                name="FarmerTel"
                value={formData.FarmerTel}
                onChange={handleInputChange}
                colorScheme="iBlue"
                isReadOnly={isExisting}
              />
              <FormErrorMessage>{errors.FarmerTel}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel>E-mail Address</FormLabel>
              <Input
                name="FarmerEmail"
                value={formData.FarmerEmail}
                onChange={handleInputChange}
                colorScheme="iBlue"
                isReadOnly={isExisting}
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={{ base: 1, md: 2, lg: 4 }}>
            <FormControl mb={4}>
              <FormLabel>วัตถุประสงค์การใช้งาน / รายละเอียดเพิ่มเติม</FormLabel>
              <Textarea
                name="UsageObjective"
                value={formData.UsageObjective}
                onChange={handleInputChange}
                colorScheme="iBlue"
                isReadOnly={isExisting}
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={{ base: 1, md: 2, lg: 4 }}>
            <FormControl mb={4}>
              <FormLabel>หลักฐานการชำระเงิน</FormLabel>
              {!isExisting ? (
                <Dropzone
                  onDrop={handleFileDrop}
                  accept={{
                    "image/jpeg": [".jpg", ".jpeg"],
                    "image/png": [".png"],
                    "application/pdf": [".pdf"],
                  }}
                  multiple
                >
                  {({ getRootProps, getInputProps }) => (
                    <Box
                      {...getRootProps()}
                      p={4}
                      border="2px dashed"
                      borderColor="gray.300"
                      borderRadius="md"
                      textAlign="center"
                      cursor="pointer"
                    >
                      <input {...getInputProps()} />
                      <Text>
                        ลากและวางไฟล์ที่นี่ หรือคลิกเพื่อเลือกไฟล์ (JPEG, PNG, PDF)
                      </Text>
               <Text fontSize="xs" color="orange.500" mt={2}>
                 ⚠️ ขนาดไฟล์ไม่ควรเกิน 700KB เพื่อป้องกันปัญหาในการบันทึกข้อมูล
               </Text>
                    </Box>
                  )}
                </Dropzone>
              ) : null}
                {(formData.paymentEvidence && formData.paymentEvidence.length > 0) && (
                  <Box mt={4}>
                    <Text fontWeight="bold" mb={3}>ไฟล์หลักฐานการชำระเงิน:</Text>
                    <Grid
                      templateColumns="repeat(auto-fill, minmax(150px, 1fr))"
                      gap={{ base: 3, md: 4 }}
                    >
                      {formData.paymentEvidence.map((file, index) => (
                        <Box
                          key={index}
                          borderWidth={1}
                          borderRadius="lg"
                          p={3}
                          bg="gray.50"
                          _hover={{ bg: "gray.100" }}
                        >
                          {file.type.startsWith("image/") ? (
                            <VStack spacing={2}>
                              <Image
                                src={file.data}
                                alt={file.name}
                                maxW="100%"
                                maxH="120px"
                                objectFit="cover"
                                borderRadius="md"
                                cursor="pointer"
                                onClick={() => handleViewFile(file)}
                                _hover={{ opacity: 0.8 }}
                              />
                              <Text fontSize="sm" fontWeight="medium" textAlign="center" noOfLines={2}>
                                {file.name}
                              </Text>
                              {file.size && (
                                <Text fontSize="xs" color="gray.500" textAlign="center">
                                  {(file.size / 1024 / 1024).toFixed(2)} MB
                                </Text>
                              )}
                              {!isExisting && (
                                <IconButton.Delete
                                  icon={faTrash}
                                  onClick={() =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      paymentEvidence: prev.paymentEvidence.filter(
                                        (_, i) => i !== index
                                      ),
                                    }))
                                  }
                                  label="ลบไฟล์"
                                  size="sm"
                                />
                              )}
                            </VStack>
                          ) : file.type === "application/pdf" ? (
                            <VStack spacing={2}>
                       <Box
                         bg="red.100"
                         p={4}
                         borderRadius="md"
                         textAlign="center"
                         cursor="pointer"
                         w="100%"
                         onClick={() => handleViewFile(file)}
                         _hover={{ bg: "red.200" }}
                       >
                         <FontAwesomeIcon icon={faFileLines} size="2x" color="#e53e3e" />
                         <Text fontSize="xs" mt={1} color="red.600">
                           PDF
                         </Text>
                       </Box>
                              <Text fontSize="sm" fontWeight="medium" textAlign="center" noOfLines={2}>
                                {file.name}
                              </Text>
                              {file.size && (
                                <Text fontSize="xs" color="gray.500" textAlign="center">
                                  {(file.size / 1024 / 1024).toFixed(2)} MB
                                </Text>
                              )}
                              {!isExisting && (
                                <IconButton.Delete
                                  icon={faTrash}
                                  onClick={() =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      paymentEvidence: prev.paymentEvidence.filter(
                                        (_, i) => i !== index
                                      ),
                                    }))
                                  }
                                  label="ลบไฟล์"
                                  size="sm"
                                />
                              )}
                            </VStack>
                          ) : (
                            <VStack spacing={2}>
                              <Box
                                bg="gray.100"
                                p={4}
                                borderRadius="md"
                                textAlign="center"
                                cursor="pointer"
                                onClick={() => handleViewFile(file)}
                                _hover={{ bg: "gray.200" }}
                              >
                                <FontAwesomeIcon icon={faFileLines} size="2x" color="#718096" />
                                <Text fontSize="xs" mt={1} color="gray.600">
                                  ไฟล์
                                </Text>
                              </Box>
                              <Text fontSize="sm" fontWeight="medium" textAlign="center" noOfLines={2}>
                                {file.name}
                              </Text>
                              {file.size && (
                                <Text fontSize="xs" color="gray.500" textAlign="center">
                                  {(file.size / 1024 / 1024).toFixed(2)} MB
                                </Text>
                              )}
                              {!isExisting && (
                                <IconButton.Delete
                                  icon={faTrash}
                                  onClick={() =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      paymentEvidence: prev.paymentEvidence.filter(
                                        (_, i) => i !== index
                                      ),
                                    }))
                                  }
                                  label="ลบไฟล์"
                                  size="sm"
                                />
                              )}
                            </VStack>
                          )}
                        </Box>
                      ))}
                    </Grid>
                  </Box>
                )}
                
         {/* แสดงข้อความเมื่อไม่มีไฟล์แนบ */}
         {isExisting && (!formData.paymentEvidence || formData.paymentEvidence.length === 0) && (
           <Box mt={4} p={4} bg="gray.50" borderRadius="md" textAlign="center">
             <Text color="gray.500" fontSize="sm">
               ยังไม่มีไฟล์หลักฐานการชำระเงิน
             </Text>
             <Text color="gray.400" fontSize="xs" mt={2}>
               หมายเหตุ: ไฟล์ขนาดใหญ่กว่า 2MB อาจไม่แสดงผลได้
             </Text>
           </Box>
         )}
            </FormControl>
          </GridItem>
        </Grid>
      </Box>

      {!isExisting && (
        <Box
          borderWidth={1}
          borderRadius="lg"
          boxShadow="lg"
          p={{ base: 3, md: 4 }}
          mb={{ base: 4, md: 6 }}
        >
        <StandardHeader
          title="เลือกวัคซีนที่ต้องการจำหน่าย"
          bgColor="iPurple.500"
          icon={faSyringe}
        />
          <Grid
            templateColumns={{
              base: "1fr",
              md: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(3, minmax(0, 1fr))",
            }}
            gap={{ base: 3, md: 4 }}
            pt={{ base: 3, md: 4 }}
          >
            <GridItem>
              <FormControl>
                <FormLabel>เลือกวัคซีน</FormLabel>
                <Select
                  name="id"
                  value={newVaccine.id}
                  onChange={handleVaccineInputChange}
                  placeholder="เลือกวัคซีน"
                  isDisabled={!formData.PickupPoint}
                >
                  {vaccineOptions.map((vaccine) => {
                    // ระหว่างที่ยังโหลดคลังไม่เสร็จ ยอดคงเหลือยังไม่น่าเชื่อถือ จึงยังไม่ทำสี
                    if (!isInventoryReady) {
                      return (
                        <option
                          key={vaccine.VaccineID}
                          value={vaccine.VaccineID}
                        >
                          {vaccine.VaccineNameTH}
                        </option>
                      );
                    }

                    const availability = vaccineAvailability.get(
                      vaccine.VaccineID
                    );
                    return (
                      <option
                        key={vaccine.VaccineID}
                        value={vaccine.VaccineID}
                        style={{
                          color: availability ? inStockColor : outOfStockColor,
                        }}
                      >
                        {availability
                          ? `${vaccine.VaccineNameTH} — คงเหลือ ${availability.quantity.toLocaleString()} ขวด (${availability.lots} Lot)`
                          : `${vaccine.VaccineNameTH} — ไม่มี Lot ที่เพิ่มได้`}
                      </option>
                    );
                  })}
                </Select>
                {!formData.PickupPoint && (
                  <Text color="red.500" fontSize="sm" mt={2}>
                    กรุณาเลือกจุดจำหน่ายก่อนเลือกวัคซีน
                  </Text>
                )}
                {formData.PickupPoint && isInventoryLoading && (
                  <Text color="gray.500" fontSize="sm" mt={2}>
                    กำลังโหลดยอดคงเหลือของคลัง...
                  </Text>
                )}
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel>Lot No.</FormLabel>
                {isInventoryLoading ? (
                  <HStack>
                    <Spinner size="sm" />
                    <Text>กำลังโหลด Lot...</Text>
                  </HStack>
                ) : (
                  <>
                    <Select
                      name="lotId"
                      value={newVaccine.lotId}
                      onChange={handleVaccineInputChange}
                      placeholder={
                        availableLots.length > 0
                          ? "เลือก Lot"
                          : "ไม่มี Lot คงเหลือ"
                      }
                      isDisabled={
                        !newVaccine.id || !formData.PickupPoint || isInventoryLoading
                      }
                    >
                      {availableLots.map((lot) => {
                        const isNearExpiry =
                          typeof lot.ExpiryDays === "number" &&
                          lot.ExpiryDays <= NEAR_EXPIRY_DAYS;
                        const expiryText = lot.ExpiryDate
                          ? new Date(lot.ExpiryDate).toLocaleDateString("th-TH", {
                              day: "numeric",
                              month: "numeric",
                              year: "numeric",
                            })
                          : "ไม่ระบุ";
                        return (
                          <option
                            key={`${lot.VaccineID}-${lot.LotID}`}
                            value={lot.LotID}
                            style={
                              isNearExpiry ? { color: nearExpiryColor } : undefined
                            }
                          >
                            {`${lot.LotID} (จำนวนคงเหลือ: ${lot.TotalQuantity.toLocaleString()} ขวด, Exp.${expiryText}${
                              isNearExpiry
                                ? `, เหลืออีก ${lot.ExpiryDays} วัน`
                                : ""
                            })`}
                          </option>
                        );
                      })}
                    </Select>
                    {selectedLotNearExpiry && (
                      <Text color="orange.600" fontSize="sm" mt={2}>
                        Lot นี้จะหมดอายุในอีก {selectedLotNearExpiry.ExpiryDays} วัน
                        กรุณาตรวจสอบก่อนจำหน่าย
                      </Text>
                    )}
                    {newVaccine.id &&
                      !isInventoryLoading &&
                      availableLots.length === 0 && (
                        <Text color="red.500" fontSize="sm" mt={2}>
                          ไม่มี Lot คงเหลือที่เพิ่มได้สำหรับวัคซีนนี้
                          (Lot ที่อยู่ในใบนี้แล้วให้แก้ไขจำนวนที่รายการเดิม)
                        </Text>
                      )}
                    {newVaccine.id && !isInventoryLoading && expiredLotCount > 0 && (
                      <Text color="gray.600" fontSize="sm" mt={2}>
                        ไม่แสดง {expiredLotCount} Lot ที่หมดอายุแล้ว
                        เนื่องจากจำหน่ายไม่ได้
                      </Text>
                    )}
                  </>
                )}
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel>จำนวน</FormLabel>
                <Input
                  name="unitreq"
                  type="number"
                  value={newVaccine.unitreq}
                  onChange={handleVaccineInputChange}
                  placeholder="จำนวน (ขวด)"
                  isDisabled={
                    !newVaccine.id || !newVaccine.lotId || isInventoryLoading
                  }
                />
              </FormControl>
            </GridItem>
          </Grid>
          <HStack mt={4} justify="flex-end">
            <Button
              leftIcon={<FontAwesomeIcon icon={faSyringe} />}
              colorScheme="green"
              onClick={handleAddVaccine}
              w={{ base: "100%", sm: "auto" }}
              isDisabled={
                !newVaccine.id ||
                !newVaccine.lotId ||
                !newVaccine.unitreq ||
                newVaccine.unitreq <= 0 ||
                isInventoryLoading
              }
            >
              เพิ่มรายการ
            </Button>
          </HStack>
        </Box>
      )}

      <Box
        borderWidth={1}
        borderRadius="lg"
        boxShadow="lg"
        p={{ base: 3, md: 4 }}
        mb={{ base: 4, md: 6 }}
      >
      <StandardHeader
          title="รายการวัคซีนที่จำหน่าย"
          bgColor="iOrange.500"
          icon={faTable}
        />
        <Box pt={{ base: 3, md: 4 }}>
          {isMobile ? (
            <VStack spacing={3} align="stretch">
              <Box textAlign="right" fontSize="sm">
                <strong>รวมจำนวน (ขวด): </strong>
                {totalBottles.toLocaleString()} |{" "}
                <strong>ราคารวมทั้งสิ้น (บาท): </strong>
                {totalValue.toLocaleString()}
              </Box>
              <VaccineDetailCards
                details={formData.VaccineDetails}
                isExisting={isExisting}
                onQuantityChange={handleQuantityChange}
                onRemove={handleRemoveVaccine}
              />
            </VStack>
          ) : (
            <Box overflowX="auto">
              <DataTable
                columns={columns}
                data={formData.VaccineDetails || []}
                customStyles={tableCustomStyles}
                noDataComponent="ไม่มีรายการวัคซีน"
                subHeader
                subHeaderComponent={
                  <Box width="100%" textAlign="right" p={2}>
                    <strong>รวมจำนวน (ขวด): </strong>
                    {totalBottles.toLocaleString()}{" "}
                    | <strong> ราคารวมทั้งสิ้น (บาท): </strong>
                    {totalValue.toLocaleString()}
                  </Box>
                }
              />
            </Box>
          )}
        </Box>
        
        {/* สรุปข้อมูลการจำหน่าย */}
        {formData.VaccineDetails && formData.VaccineDetails.length > 0 && (
          <Box
            mt={5}
            p={{ base: 3, md: 5 }}
            bg="blue.50"
            borderRadius="lg"
            borderWidth={1}
            borderColor="blue.200"
          >
            <Heading size="md" mb={4} color="blue.700">
              สรุปข้อมูลการจำหน่าย
            </Heading>
            <Grid
              templateColumns={{
                base: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(4, minmax(0, 1fr))",
              }}
              gap={{ base: 3, md: 6 }}
            >
              <GridItem>
                <Box
                  p={{ base: 3, md: 4 }}
                  bg="white"
                  borderRadius="md"
                  borderWidth={1}
                  borderColor="blue.100"
                  textAlign="center"
                >
                  <Text fontSize="sm" color="gray.600" mb={2}>
                    จำนวนชนิดวัคซีน
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                    {new Set(formData.VaccineDetails.map((d) => d.VaccineID)).size}
                  </Text>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    ชนิด
                  </Text>
                </Box>
              </GridItem>
              <GridItem>
                <Box
                  p={{ base: 3, md: 4 }}
                  bg="white"
                  borderRadius="md"
                  borderWidth={1}
                  borderColor="blue.100"
                  textAlign="center"
                >
                  <Text fontSize="sm" color="gray.600" mb={2}>
                    จำนวนขวด
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="green.600">
                    {formData.VaccineDetails.reduce(
                      (sum, detail) => sum + (detail.RequestQuantity || 0),
                      0
                    ).toLocaleString()}
                  </Text>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    ขวด
                  </Text>
                </Box>
              </GridItem>
              <GridItem>
                <Box
                  p={{ base: 3, md: 4 }}
                  bg="white"
                  borderRadius="md"
                  borderWidth={1}
                  borderColor="blue.100"
                  textAlign="center"
                >
                  <Text fontSize="sm" color="gray.600" mb={2}>
                    จำนวนโดส
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="purple.600">
                    {formData.VaccineDetails.reduce(
                      (sum, detail) => sum + (detail.DoseCount || 0),
                      0
                    ).toLocaleString()}
                  </Text>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    โดส
                  </Text>
                </Box>
              </GridItem>
              <GridItem>
                <Box
                  p={{ base: 3, md: 4 }}
                  bg="white"
                  borderRadius="md"
                  borderWidth={1}
                  borderColor="blue.100"
                  textAlign="center"
                >
                  <Text fontSize="sm" color="gray.600" mb={2}>
                    ราคารวม
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="orange.600">
                    {formData.VaccineDetails.reduce(
                      (sum, detail) => sum + (detail.TotalPrice || 0),
                      0
                    ).toLocaleString()}
                  </Text>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    บาท
                  </Text>
                </Box>
              </GridItem>
            </Grid>
          </Box>
        )}
      </Box>

      <Flex
        gap={3}
        mb={{ base: 2, md: 4 }}
        direction={{ base: "column", sm: "row" }}
        justify="flex-end"
      >
        <IconButton.Back
          onClick={() => navigate("/vaccinesalelist")}
          label="ย้อนกลับ"
        />
        {isExisting && (
          <Button
            leftIcon={<FontAwesomeIcon icon={faPrint} />}
            colorScheme="blue"
            onClick={printReceipt}
          >
            พิมพ์ใบเสร็จรับเงิน
          </Button>
        )}
        {!isExisting && (
          <IconButton.Save
            onClick={handleOpenConfirmModal} // เปลี่ยนให้เปิด Modal แทนการบันทึกทันที
            isLoading={isLoading}
            isDisabled={!isFormValid()}
          />
        )}
      </Flex>

        {/* Modal for viewing files */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          size={{ base: "full", md: "4xl", xl: "6xl" }}
          scrollBehavior="inside"
          isCentered
        >
          <ModalOverlay />
          <ModalContent maxW={{ base: "100%", md: "90vw" }} maxH={{ base: "100%", md: "90vh" }}>
            <ModalHeader pr={12}>
              <HStack spacing={3} minW={0}>
                <FontAwesomeIcon icon={faFileLines} />
                <Text fontSize={{ base: "md", md: "lg" }} noOfLines={1}>
                  {selectedFile?.name}
                </Text>
              </HStack>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={4}>
              {selectedFile?.type?.startsWith("image/") ? (
                <Box textAlign="center">
                  <Image
                    src={selectedFile.data}
                    alt={selectedFile.name}
                    maxW="100%"
                    maxH={{ base: "65vh", md: "70vh" }}
                    objectFit="contain"
                    borderRadius="md"
                    boxShadow="lg"
                    mx="auto"
                  />
                </Box>
              ) : selectedFile?.type === "application/pdf" && previewUrl ? (
                <Box
                  as="iframe"
                  title={selectedFile.name}
                  src={previewUrl}
                  w="100%"
                  h={{ base: "70vh", md: "75vh" }}
                  borderWidth={1}
                  borderRadius="md"
                />
              ) : (
                <Box textAlign="center" py={8}>
                  <FontAwesomeIcon icon={faFileLines} size="3x" color="#718096" />
                  <Text fontSize="lg" fontWeight="bold" mt={3} color="gray.600">
                    ไม่สามารถแสดงตัวอย่างไฟล์นี้ได้
                  </Text>
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    กรุณากดดาวน์โหลดเพื่อเปิดด้วยโปรแกรมในเครื่อง
                  </Text>
                </Box>
              )}
            </ModalBody>
            <ModalFooter
              gap={3}
              flexWrap="wrap"
              justifyContent={{ base: "stretch", sm: "flex-end" }}
            >
              <IconButton.Download
                onClick={() => downloadEvidenceFile(selectedFile)}
                label="ดาวน์โหลดไฟล์"
                w={{ base: "100%", sm: "auto" }}
              />
              <IconButton.Close
                onClick={() => setIsModalOpen(false)}
                w={{ base: "100%", sm: "auto" }}
              />
            </ModalFooter>
          </ModalContent>
        </Modal>

      {/* Modal for confirming save */}
      <Modal isOpen={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>ยืนยันการบันทึกใบจำหน่าย</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="lg" mb={4} color="red.600" fontWeight="bold">
              กรุณาตรวจสอบข้อมูลก่อนบันทึก หลังจากบันทึกแล้วจะไม่สามารถแก้ไขรายการได้
            </Text>
            <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={3}>
              <Box>
                <Text fontWeight="bold">จุดจำหน่าย:</Text>
                <Text>{confirmSummary.pickupPointName}</Text>
              </Box>
              <Box>
                <Text fontWeight="bold">วันที่จำหน่าย:</Text>
                <Text>{confirmSummary.requestDateText}</Text>
              </Box>
              <Box>
                <Text fontWeight="bold">ชื่อเกษตรกร:</Text>
                <Text>{formData.FarmerName || "-"}</Text>
              </Box>
              <Box>
                <Text fontWeight="bold">เลขประจำตัวประชาชน:</Text>
                <Text color={formData.FarmerID ? undefined : "orange.600"}>
                  {formData.FarmerID || "ไม่ได้ระบุ"}
                </Text>
              </Box>
              <Box>
                <Text fontWeight="bold">เบอร์โทรติดต่อ:</Text>
                <Text>{formData.FarmerTel || "-"}</Text>
              </Box>
              <Box>
                <Text fontWeight="bold">หลักฐานการชำระเงิน:</Text>
                <Text
                  color={confirmSummary.evidenceCount > 0 ? undefined : "orange.600"}
                >
                  {confirmSummary.evidenceCount > 0
                    ? `${confirmSummary.evidenceCount} ไฟล์`
                    : "ยังไม่ได้แนบไฟล์"}
                </Text>
              </Box>
            </Grid>
            <Box mt={4}>
              <Text fontWeight="bold">รายการวัคซีนที่จำหน่าย:</Text>
              {formData.VaccineDetails.length > 0 ? (
                <VStack align="start" mt={2} spacing={1}>
                  {formData.VaccineDetails.map((detail, index) => (
                    <Text key={index}>
                      {`${detail.name} (Lot: ${detail.LotID}) ${detail.RequestQuantity.toLocaleString()} ขวด = ${detail.TotalPrice.toLocaleString()} บาท`}
                    </Text>
                  ))}
                </VStack>
              ) : (
                <Text>ไม่มีรายการวัคซีน</Text>
              )}
            </Box>
            <Box
              mt={4}
              p={3}
              bg="gray.50"
              borderWidth={1}
              borderColor="gray.200"
              borderRadius="md"
            >
              <Text fontWeight="bold" fontSize="lg">
                ยอดเงินที่ต้องเรียกเก็บ: {confirmSummary.totalValue.toLocaleString()} บาท
              </Text>
              <Text fontSize="sm" color="gray.600">
                รวม {confirmSummary.totalBottles.toLocaleString()} ขวด
                จาก {formData.VaccineDetails.length} รายการ
              </Text>
            </Box>
            <Checkbox
              mt={4}
              isChecked={isConsentGiven}
              onChange={(e) => setIsConsentGiven(e.target.checked)}
            >
              <Text fontSize="sm">
                เกษตรกรยินยอมให้จัดเก็บและใช้ข้อมูลส่วนบุคคล
                (ชื่อ เบอร์โทร อีเมล และเลขประจำตัวประชาชน)
                เพื่อการออกใบเสร็จและการติดตามการใช้วัคซีน
              </Text>
            </Checkbox>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="backScheme"
              mr={3}
              onClick={() => setIsConfirmModalOpen(false)}
            >
              ยกเลิก
            </Button>
            <Button
              colorScheme="saveScheme"
              onClick={handleSave}
              isLoading={isLoading}
              isDisabled={!isConsentGiven}
            >
              ยืนยัน
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default VaccineSaleForm;
